import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2 } from "lucide-react";
import { api, formatApiError, prettyTime } from "@/lib/api";
import { DesignsTab, FabricsTab } from "@/components/AdminDesignTabs";
import LeadsTab from "@/components/AdminLeadsTab";
import TrafficTab from "@/components/AdminTrafficTab";

const STATUS_FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];
const STATUS_STYLES = {
  pending: "border-[#C59B27]/50 text-[#C59B27]",
  confirmed: "border-emerald-500/40 text-emerald-400",
  completed: "border-stone-500/40 text-stone-400",
  cancelled: "border-[#8B2626]/50 text-[#E8A0A0]",
  new: "border-sky-500/40 text-sky-400",
  contacted: "border-[#C59B27]/50 text-[#C59B27]",
  closed: "border-stone-500/40 text-stone-400",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#121110]" data-testid="admin-loading">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">Opening the appointment book…</p>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#121110] text-[#FFFFFF]" data-testid="admin-dashboard">
      <header className="border-b border-[#2A2825]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10">
          <div className="flex items-center gap-4">
            <img src="/images/logo-patch.png" alt="Bespoke Maestro" className="h-12 w-auto rounded-sm" data-testid="admin-logo" />
            <div>
              <p className="font-serif text-xl font-semibold">Bespoke Maestro</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#858078]">Atelier Dashboard</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-[#2A2825] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078] transition-colors hover:border-[#C59B27] hover:text-[#C59B27]"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10">
        <div className="flex gap-2 border-b border-[#2A2825]" data-testid="admin-tabs">
          {[
            ["leads", "Leads"],
            ["traffic", "Traffic"],
            ["bookings", "Appointments"],
            ["designs", "Design Requests"],
            ["fabrics", "Fabrics"],
            ["weddings", "Weddings"],
            ["availability", "Weekly Hours"],
            ["blocked", "Blocked Dates"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
                tab === key
                  ? "border-[#C59B27] text-[#FFFFFF]"
                  : "border-transparent text-[#858078] hover:text-[#FFFFFF]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab />}
        {tab === "traffic" && <TrafficTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "designs" && <DesignsTab />}
        {tab === "fabrics" && <FabricsTab />}
        {tab === "weddings" && <WeddingsTab />}
        {tab === "availability" && <AvailabilityTab />}
        {tab === "blocked" && <BlockedTab />}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/bookings${filter !== "all" ? `?status=${filter}` : ""}`),
      ]);
      setStats(s.data);
      setBookings(b.data.bookings);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (bookingId, status) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}`, { status });
      toast.success(`Appointment ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-10">
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
          {[
            ["Total", stats.total],
            ["Pending", stats.pending],
            ["Upcoming", stats.upcoming],
            ["Completed", stats.completed],
            ["Top Cloth", stats.top_fabric],
          ].map(([label, value]) => (
            <div key={label} className="border border-[#2A2825] bg-[#1A1917] p-6" data-testid={`admin-stat-${label.toLowerCase().replace(" ", "-")}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">{label}</p>
              <p className="mt-2 font-serif text-3xl font-light text-[#FFFFFF]">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-booking-filters">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`admin-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              filter === f
                ? "border-[#C59B27] text-[#C59B27]"
                : "border-[#2A2825] text-[#858078] hover:text-[#FFFFFF]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-bookings-list">
        {loading && <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">Loading…</p>}
        {!loading && bookings.length === 0 && (
          <p className="border border-[#2A2825] bg-[#1A1917] py-14 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]" data-testid="admin-bookings-empty">
            No appointments in this view
          </p>
        )}
        {!loading &&
          bookings.map((b) => (
            <div
              key={b.booking_id}
              data-testid={`admin-booking-${b.booking_id}`}
              className="grid gap-4 border border-[#2A2825] bg-[#1A1917] p-6 lg:grid-cols-12 lg:items-center"
            >
              <div className="lg:col-span-3">
                <p className="font-serif text-lg text-[#FFFFFF]">{b.name}</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#C59B27]">{b.booking_id}</p>
                <p className="mt-1 text-xs text-[#858078]">{b.email} · {b.phone}</p>
              </div>
              <div className="lg:col-span-3">
                <p className="text-sm text-[#FFFFFF]">{b.service_name}</p>
                <p className="mt-1 text-xs text-[#858078]">
                  Cloth: {b.fabric || "Open to guidance"} · {b.location}
                </p>
                {b.notes && <p className="mt-1 text-xs italic text-[#858078]">"{b.notes}"</p>}
              </div>
              <div className="lg:col-span-2">
                <p className="text-sm text-[#FFFFFF]">{b.date}</p>
                <p className="text-xs text-[#858078]">{prettyTime(b.time)}</p>
              </div>
              <div className="lg:col-span-1">
                <span className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[b.status]}`}>
                  {b.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 lg:col-span-3 lg:justify-end">
                {b.status === "pending" && (
                  <Action testid={`admin-confirm-booking-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "confirmed")} label="Confirm" accent />
                )}
                {(b.status === "pending" || b.status === "confirmed") && (
                  <>
                    <Action testid={`admin-complete-booking-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "completed")} label="Complete" />
                    <Action testid={`admin-cancel-booking-${b.booking_id}`} onClick={() => setStatus(b.booking_id, "cancelled")} label="Cancel" danger />
                  </>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function Action({ testid, onClick, label, accent, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors active:scale-95 ${
        accent
          ? "border-[#C59B27] text-[#C59B27] hover:bg-[#C59B27] hover:text-[#121110]"
          : danger
            ? "border-[#8B2626]/60 text-[#E8A0A0] hover:bg-[#8B2626] hover:text-white"
            : "border-[#2A2825] text-[#858078] hover:border-[#FFFFFF] hover:text-[#FFFFFF]"
      }`}
    >
      {label}
    </button>
  );
}

function AvailabilityTab() {
  const [schedule, setSchedule] = useState(null);
  const [weekdays, setWeekdays] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/availability-settings")
      .then((r) => {
        setSchedule(r.data.schedule);
        setWeekdays(r.data.weekdays);
        setAllSlots(r.data.all_slots);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const toggle = (day, slot) => {
    const current = schedule[day] || [];
    const next = current.includes(slot) ? current.filter((s) => s !== slot) : [...current, slot];
    setSchedule({ ...schedule, [day]: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/availability-settings", { schedule });
      toast.success("Weekly hours updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!schedule) return <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">Loading…</p>;

  return (
    <div className="mt-10" data-testid="admin-availability-manager">
      <p className="max-w-xl text-sm font-light leading-relaxed text-[#858078]">
        Toggle the hours you take appointments on each day of the week. Clients see these slots in
        real time; booked hours disappear automatically.
      </p>
      <div className="mt-8 space-y-3">
        {weekdays.map((name, i) => (
          <div key={name} className="grid items-center gap-4 border border-[#2A2825] bg-[#1A1917] p-5 sm:grid-cols-12" data-testid={`admin-schedule-${name.toLowerCase()}`}>
            <p className="font-serif text-lg sm:col-span-3">{name}</p>
            <div className="flex flex-wrap gap-2 sm:col-span-9">
              {allSlots.map((slot) => {
                const active = (schedule[String(i)] || []).includes(slot);
                return (
                  <button
                    key={slot}
                    data-testid={`admin-slot-${name.toLowerCase()}-${slot.replace(":", "")}`}
                    onClick={() => toggle(String(i), slot)}
                    className={`border px-4 py-2 font-mono text-xs tracking-[0.1em] transition-colors active:scale-95 ${
                      active
                        ? "border-[#C59B27] bg-[#C59B27]/10 text-[#C59B27]"
                        : "border-[#2A2825] text-[#5A564F] hover:text-[#858078]"
                    }`}
                  >
                    {prettyTime(slot)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <button
        data-testid="admin-save-schedule-button"
        onClick={save}
        disabled={saving}
        className="mt-8 bg-[#C59B27] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#121110] transition-colors duration-200 hover:bg-[#FFFFFF] active:scale-95 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Weekly Hours"}
      </button>
    </div>
  );
}

function BlockedTab() {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/blocked-dates");
      setDates(data.blocked_dates);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!date) return;
    try {
      await api.post("/admin/blocked-dates", { date, reason });
      toast.success("Date blocked.");
      setDate("");
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/admin/blocked-dates/${d}`);
      toast.success("Date reopened.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-10" data-testid="admin-blocked-manager">
      <p className="max-w-xl text-sm font-light leading-relaxed text-[#858078]">
        Block days for travel, trunk shows or private events — no client can book a blocked date.
      </p>
      <div className="mt-8 flex flex-wrap items-end gap-4 border border-[#2A2825] bg-[#1A1917] p-6">
        <label className="block">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Date</span>
          <input
            data-testid="admin-block-date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-[#2A2825] bg-[#121110] px-4 py-3 text-sm text-[#FFFFFF] outline-none focus:border-[#C59B27]"
          />
        </label>
        <label className="block flex-1 min-w-[200px]">
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Reason (optional)</span>
          <input
            data-testid="admin-block-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Trunk show in Los Angeles"
            className="w-full border border-[#2A2825] bg-[#121110] px-4 py-3 text-sm text-[#FFFFFF] outline-none focus:border-[#C59B27]"
          />
        </label>
        <button
          data-testid="admin-block-date-button"
          onClick={add}
          className="inline-flex items-center gap-2 bg-[#C59B27] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#121110] transition-colors hover:bg-[#FFFFFF] active:scale-95"
        >
          <Plus size={14} /> Block
        </button>
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-blocked-list">
        {!loading && dates.length === 0 && (
          <p className="border border-[#2A2825] bg-[#1A1917] py-10 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">
            No blocked dates
          </p>
        )}
        {dates.map((d) => (
          <div key={d.date} className="flex items-center justify-between border border-[#2A2825] bg-[#1A1917] px-6 py-4" data-testid={`admin-blocked-${d.date}`}>
            <div>
              <p className="font-serif text-lg">{d.date}</p>
              {d.reason && <p className="text-xs text-[#858078]">{d.reason}</p>}
            </div>
            <button
              data-testid={`admin-unblock-${d.date}`}
              onClick={() => remove(d.date)}
              className="inline-flex items-center gap-2 border border-[#2A2825] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#858078] transition-colors hover:border-[#8B2626] hover:text-[#E8A0A8]"
            >
              <Trash2 size={13} /> Reopen
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeddingsTab() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/wedding-inquiries");
      setInquiries(data.inquiries);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (inquiryId, status) => {
    try {
      await api.patch(`/admin/wedding-inquiries/${inquiryId}`, { status });
      toast.success(`Inquiry marked ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-10" data-testid="admin-weddings-tab">
      <p className="max-w-xl text-sm font-light leading-relaxed text-[#858078]">
        Wedding concierge inquiries — reach out personally, then mark each inquiry as you move it along.
      </p>
      <div className="mt-8 space-y-3" data-testid="admin-weddings-list">
        {loading && <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">Loading…</p>}
        {!loading && inquiries.length === 0 && (
          <p className="border border-[#2A2825] bg-[#1A1917] py-14 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]" data-testid="admin-weddings-empty">
            No wedding inquiries yet
          </p>
        )}
        {!loading &&
          inquiries.map((w) => (
            <div
              key={w.inquiry_id}
              data-testid={`admin-wedding-${w.inquiry_id}`}
              className="grid gap-4 border border-[#2A2825] bg-[#1A1917] p-6 lg:grid-cols-12 lg:items-center"
            >
              <div className="lg:col-span-3">
                <p className="font-serif text-lg text-[#FFFFFF]">{w.name}</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#C59B27]">{w.inquiry_id}</p>
                <p className="mt-1 text-xs text-[#858078]">{w.email} · {w.phone}</p>
              </div>
              <div className="lg:col-span-4">
                <p className="text-sm text-[#FFFFFF]">{w.role} · party of {w.party_size}</p>
                <p className="mt-1 text-xs text-[#858078]">
                  {w.wedding_date ? `Wedding: ${w.wedding_date}` : "Date to be decided"}
                  {w.venue ? ` · ${w.venue}` : ""}
                </p>
                {w.notes && <p className="mt-1 text-xs italic text-[#858078]">"{w.notes}"</p>}
              </div>
              <div className="lg:col-span-2">
                <span className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLES[w.status]}`}>
                  {w.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 lg:col-span-3 lg:justify-end">
                {w.status === "new" && (
                  <Action testid={`admin-contact-wedding-${w.inquiry_id}`} onClick={() => setStatus(w.inquiry_id, "contacted")} label="Mark Contacted" accent />
                )}
                {(w.status === "new" || w.status === "contacted") && (
                  <Action testid={`admin-close-wedding-${w.inquiry_id}`} onClick={() => setStatus(w.inquiry_id, "closed")} label="Close" />
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
